import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SITE_GLASS_TILE } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

type Overview = {
  totalContacts: number;
  newLeadsThisMonth: number;
  convertedCount: number;
  conversionRate: number | null;
  dealsLinkedDistinct: number;
};

/** CRM HQ — KPIs for institutional fundraising + sourcing rhythms. */

export function CrmOverview() {
  const { session } = useAuth();
  const token = session?.access_token;
  const [data, setData] = useState<Overview | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const o = await apiFetch<Overview>('/api/crm/overview', { token });
        setData(o);
      } catch {
        setErr('Could not load CRM overview.');
      }
    })();
  }, [token]);

  if (err) return <p className="text-sm text-red-400">{err}</p>;
  if (!data) return <p className="text-sm text-[var(--home-muted-on-dark)]">Loading CRM KPIs…</p>;

  const tiles = [
    { label: 'Total CRM contacts', value: data.totalContacts },
    { label: 'Added this month', value: data.newLeadsThisMonth },
    { label: 'Converted (stage)', value: data.convertedCount },
    { label: 'Pipeline conversion rate', value: `${data.conversionRate ?? 0}%` },
    {
      label: 'Distinct offerings linked via CRM graph',
      value: data.dealsLinkedDistinct,
    },
  ];

  return (
    <div className="space-y-10">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-[var(--home-text-on-dark)]">CRM command center</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[var(--home-muted-on-dark)]">
          Institutional capital raising lives here — relationships, attribution, progression to commitment, and
          alignment with underwriting coverage. Drill into contacts from the navigator or visualize stage velocity on the{' '}
          <Link className="font-medium text-[var(--home-miami-blue)] hover:brightness-110" to="/admin/crm/pipeline">
            Pipeline board
          </Link>
          .
        </p>
      </div>
      <section className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {tiles.map((t) => (
          <div key={t.label} className={`px-5 py-4 ${SITE_GLASS_TILE}`}>
            <p className="text-[11px] uppercase leading-snug tracking-wide text-[var(--home-muted-on-dark)]">
              {t.label}
            </p>
            <p className="mt-3 text-2xl font-semibold tabular-nums text-[var(--home-text-on-dark)]">{t.value}</p>
          </div>
        ))}
      </section>
      <div className="flex flex-wrap gap-4">
        <Link
          to="/admin/crm/contacts"
          className="inline-flex items-center rounded-xl bg-[var(--home-miami-blue)] px-6 py-2.5 text-sm font-semibold text-[var(--home-zone-void)] shadow-md shadow-black/20 transition hover:brightness-105"
        >
          Open CRM directory
        </Link>
        <Link
          to="/admin/crm/deals"
          className="inline-flex items-center rounded-xl border border-white/[0.12] bg-[var(--home-zone-dark)]/50 px-6 py-2.5 text-sm font-medium text-[var(--home-text-on-dark)] ring-1 ring-white/[0.06] backdrop-blur-sm transition hover:bg-[var(--home-zone-dark)]/65"
        >
          Pipeline deals & tools
        </Link>
        <Link
          to="/admin/crm/pipeline"
          className="inline-flex items-center rounded-xl border border-white/[0.1] px-6 py-2.5 text-sm font-medium text-[var(--home-text-on-dark)] transition hover:bg-white/[0.06]"
        >
          View Kanban workflow
        </Link>
      </div>
    </div>
  );
}
